import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import usePlayerNameBlacklist from '../../hooks/usePlayerNameBlacklist';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const TERM_MAX_LENGTH = 60;
const TERM_ALLOWED_CHARS_REGEX = /^[\x20-\x7E\u00A0-\u024F\u0300-\u036F\u1E00-\u1EFF]+$/u;

function normalizeTermClient(term) {
  return String(term || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

export default function PlayerNameBlacklistManagement() {
  const authCtx = useContext(AuthContext);
  const {
    isLoading,
    getEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    getEditSuggestions,
    markEditSuggestionHandled,
    getSuggestions,
    createSuggestion,
    updateSuggestion,
    deleteSuggestion,
    approveSuggestion,
    rejectSuggestion,
  } = usePlayerNameBlacklist();

  const [entries, setEntries] = useState([]);
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showOnlyPendingEditSuggestions, setShowOnlyPendingEditSuggestions] = useState(true);
  const [showOnlyPendingSuggestions, setShowOnlyPendingSuggestions] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [newEntryMatchWholeWordOnly, setNewEntryMatchWholeWordOnly] = useState(false);
  const [editDialogMatchWholeWordOnly, setEditDialogMatchWholeWordOnly] = useState(false);
  const [prepareDialogMatchWholeWordOnly, setPrepareDialogMatchWholeWordOnly] = useState(false);
  const termInputRef = useRef(null);
  const editInputRef = useRef(null);
  const editSuggestionCriticalRef = useRef(null);

  const [editState, setEditState] = useState({ open: false, suggestion: null });
  const [editSuggestionState, setEditSuggestionState] = useState({ open: false, editSuggestion: null });

  const canManage = authCtx.checkRight('playerNameBlacklist:manage');
  const canSuggest = canManage || authCtx.checkRight('playerNameBlacklist:suggest');

  const sortedEntries = useMemo(() => {
    const list = Array.isArray(entries) ? entries : [];
    return [...list].sort((a, b) =>
      String(a?.termNormalized || a?.term || '').localeCompare(String(b?.termNormalized || b?.term || ''), undefined, {
        sensitivity: 'base',
      })
    );
  }, [entries]);

  const sortedSuggestions = useMemo(() => {
    const list = Array.isArray(suggestions) ? suggestions : [];
    return [...list].sort((a, b) => {
      const aTs = a?.meta?.requestedAt ? new Date(a.meta.requestedAt).getTime() : 0;
      const bTs = b?.meta?.requestedAt ? new Date(b.meta.requestedAt).getTime() : 0;
      if (aTs !== bTs) return bTs - aTs;
      return String(a?._id || '').localeCompare(String(b?._id || ''));
    });
  }, [suggestions]);

  const sortedEditSuggestions = useMemo(() => {
    const list = Array.isArray(editSuggestions) ? editSuggestions : [];
    return [...list].sort((a, b) => {
      const aTs = a?.meta?.createdAt ? new Date(a.meta.createdAt).getTime() : 0;
      const bTs = b?.meta?.createdAt ? new Date(b.meta.createdAt).getTime() : 0;
      if (aTs !== bTs) return bTs - aTs;
      return String(a?._id || '').localeCompare(String(b?._id || ''));
    });
  }, [editSuggestions]);

  const fetchEntries = useCallback(async () => {
    const resp = await getEntries();
    setEntries(resp?.data || []);
  }, [getEntries]);

  const fetchEditSuggestions = useCallback(async () => {
    if (!canSuggest) return;
    const resp = await getEditSuggestions({
      status: showOnlyPendingEditSuggestions ? 'pending' : undefined,
    });
    setEditSuggestions(resp?.data || []);
  }, [canSuggest, getEditSuggestions, showOnlyPendingEditSuggestions]);

  const fetchSuggestions = useCallback(async () => {
    if (!canSuggest) return;
    const resp = await getSuggestions({ status: showOnlyPendingSuggestions ? 'pending' : undefined });
    setSuggestions(resp?.data || []);
  }, [canSuggest, getSuggestions, showOnlyPendingSuggestions]);

  useEffect(() => {
    fetchEntries().catch(() => {});
  }, [fetchEntries]);

  useEffect(() => {
    fetchEditSuggestions().catch(() => {});
  }, [fetchEditSuggestions]);

  useEffect(() => {
    fetchSuggestions().catch(() => {});
  }, [fetchSuggestions]);

  const validateTermClient = termRaw => {
    const term = String(termRaw || '').trim();
    if (!term) return { ok: false, message: 'Bitte ein Wort eingeben' };
    if (term.length > TERM_MAX_LENGTH)
      return { ok: false, message: `Maximal ${TERM_MAX_LENGTH} Zeichen erlaubt` };
    if (!TERM_ALLOWED_CHARS_REGEX.test(term))
      return { ok: false, message: 'Nur Zeichen ohne Emojis erlaubt (Umlaute und Akzente sind ok)' };
    const normalized = normalizeTermClient(term);
    if (!normalized) return { ok: false, message: 'Bitte Buchstaben/Zahlen verwenden' };
    return { ok: true, term };
  };

  const clearTermInput = () => {
    if (termInputRef.current) {
      termInputRef.current.value = '';
    }
  };

  const handleSubmit = useCallback(async () => {
    const raw = termInputRef.current?.value ?? '';
    const v = validateTermClient(raw);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }
    try {
      if (canManage) {
        await createEntry({ term: v.term, matchWholeWordOnly: newEntryMatchWholeWordOnly });
        clearTermInput();
        setNewEntryMatchWholeWordOnly(false);
        toast.success('Eintrag hinzugefügt');
        await fetchEntries();
      } else if (canSuggest) {
        await createSuggestion({ term: v.term, matchWholeWordOnly: newEntryMatchWholeWordOnly });
        clearTermInput();
        setNewEntryMatchWholeWordOnly(false);
        toast.success('Vorschlag gesendet');
        await fetchSuggestions();
      } else {
        toast.error('Keine Berechtigung');
      }
    } catch (e) {
      const code = String(e?.code || '').trim();
      if (code === 'BLACKLIST_ENTRY_EXISTS') {
        toast.error('Eintrag existiert bereits');
        return;
      }
      if (code === 'BLACKLIST_SUGGESTION_EXISTS') {
        toast.error('Vorschlag existiert bereits');
        return;
      }
      toast.error(canManage ? 'Fehler beim Hinzufügen' : 'Fehler beim Senden des Vorschlags');
    }
  }, [
    canManage,
    canSuggest,
    createEntry,
    createSuggestion,
    fetchEntries,
    fetchSuggestions,
    newEntryMatchWholeWordOnly,
  ]);

  const handleToggleActive = useCallback(
    async entry => {
      if (!entry?._id) return;
      if (!canManage) return;
      const id = String(entry._id);
      const key = `entry:${id}`;
      setBusyKey(key);
      try {
        await updateEntry(id, { active: entry.active !== true });
        await fetchEntries();
      } catch (e) {
        // errors are toasted in the hook
      } finally {
        setBusyKey(null);
      }
    },
    [canManage, fetchEntries, updateEntry]
  );

  const handleToggleEntryMatchWholeWord = useCallback(
    async entry => {
      if (!entry?._id) return;
      if (!canManage) return;
      const id = String(entry._id);
      const key = `entry:${id}`;
      setBusyKey(key);
      try {
        await updateEntry(id, { matchWholeWordOnly: entry.matchWholeWordOnly !== true });
        await fetchEntries();
      } catch (e) {
        // errors are toasted in the hook
      } finally {
        setBusyKey(null);
      }
    },
    [canManage, fetchEntries, updateEntry]
  );

  const handleDelete = useCallback(
    async entry => {
      if (!entry?._id) return;
      if (!canManage) return;
      const id = String(entry._id);
      const key = `entry:${id}`;
      setBusyKey(key);
      try {
        await deleteEntry(id);
        toast.success('Eintrag gelöscht');
        await fetchEntries();
      } catch (e) {
        // errors are toasted in the hook
      } finally {
        setBusyKey(null);
      }
    },
    [canManage, deleteEntry, fetchEntries]
  );

  const handleApproveSuggestion = useCallback(
    async suggestion => {
      if (!suggestion?._id) return;
      if (!canManage) return;
      const id = String(suggestion._id);
      const key = `suggestion:${id}`;
      setBusyKey(key);
      try {
        await approveSuggestion(id);
        toast.success('Vorschlag angenommen');
        await Promise.all([fetchSuggestions(), fetchEntries()]);
      } catch (e) {
        const code = String(e?.code || '').trim();
        if (code === 'BLACKLIST_ENTRY_EXISTS') {
          toast.error('Eintrag existiert bereits');
          return;
        }
        toast.error('Fehler beim Annehmen des Vorschlags');
      } finally {
        setBusyKey(null);
      }
    },
    [approveSuggestion, canManage, fetchEntries, fetchSuggestions]
  );

  const handleRejectSuggestion = useCallback(
    async suggestion => {
      if (!suggestion?._id) return;
      if (!canManage) return;
      const id = String(suggestion._id);
      const key = `suggestion:${id}`;
      setBusyKey(key);
      try {
        await rejectSuggestion(id);
        toast.success('Vorschlag abgelehnt');
        await fetchSuggestions();
      } catch (e) {
        toast.error('Fehler beim Ablehnen des Vorschlags');
      } finally {
        setBusyKey(null);
      }
    },
    [canManage, fetchSuggestions, rejectSuggestion]
  );

  const openEditSuggestion = suggestion => {
    if (!suggestion?._id) return;
    setEditState({ open: true, suggestion });
    setEditDialogMatchWholeWordOnly(suggestion?.matchWholeWordOnly === true);
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.value = suggestion?.term || '';
      }
    }, 0);
  };

  const closeEditSuggestion = () => {
    setEditState({ open: false, suggestion: null });
  };

  const handleSaveEditedSuggestion = useCallback(async () => {
    const s = editState.suggestion;
    if (!s?._id) return;
    const id = String(s._id);
    const raw = editInputRef.current?.value ?? '';
    const v = validateTermClient(raw);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }
    const key = `suggestion:${id}`;
    setBusyKey(key);
    try {
      await updateSuggestion(id, { term: v.term, matchWholeWordOnly: editDialogMatchWholeWordOnly });
      toast.success('Vorschlag gespeichert');
      closeEditSuggestion();
      await fetchSuggestions();
    } catch (e) {
      const code = String(e?.code || '').trim();
      if (code === 'BLACKLIST_ENTRY_EXISTS') {
        toast.error('Eintrag existiert bereits');
        return;
      }
      if (code === 'BLACKLIST_SUGGESTION_EXISTS') {
        toast.error('Vorschlag existiert bereits');
        return;
      }
      if (code === 'BLACKLIST_SUGGESTION_NOT_PENDING') {
        toast.error('Vorschlag ist nicht mehr offen');
        closeEditSuggestion();
        await fetchSuggestions();
        return;
      }
      toast.error('Fehler beim Speichern des Vorschlags');
    } finally {
      setBusyKey(null);
    }
  }, [editDialogMatchWholeWordOnly, editState.suggestion, fetchSuggestions, updateSuggestion]);

  const handleToggleSuggestionMatchWholeWord = useCallback(
    async suggestion => {
      if (!suggestion?._id) return;
      if (String(suggestion?.status || '') !== 'pending') return;
      const id = String(suggestion._id);
      const key = `suggestion:${id}`;
      setBusyKey(key);
      try {
        await updateSuggestion(id, {
          term: suggestion.term,
          matchWholeWordOnly: suggestion.matchWholeWordOnly !== true,
        });
        await fetchSuggestions();
      } catch (e) {
        // errors are toasted in the hook
      } finally {
        setBusyKey(null);
      }
    },
    [fetchSuggestions, updateSuggestion]
  );

  const handleDeleteSuggestion = useCallback(
    async suggestion => {
      if (!suggestion?._id) return;
      const id = String(suggestion._id);
      const key = `suggestion:${id}`;
      setBusyKey(key);
      try {
        await deleteSuggestion(id);
        toast.success('Vorschlag gelöscht');
        await fetchSuggestions();
      } catch (e) {
        const code = String(e?.code || '').trim();
        if (code === 'BLACKLIST_SUGGESTION_NOT_PENDING') {
          toast.error('Vorschlag ist nicht mehr offen');
          await fetchSuggestions();
          return;
        }
        toast.error('Fehler beim Löschen des Vorschlags');
      } finally {
        setBusyKey(null);
      }
    },
    [deleteSuggestion, fetchSuggestions]
  );

  const openEditSuggestionPrep = es => {
    if (!es?._id) return;
    setEditSuggestionState({ open: true, editSuggestion: es });
    setPrepareDialogMatchWholeWordOnly(false);
    setTimeout(() => {
      if (editSuggestionCriticalRef.current) {
        editSuggestionCriticalRef.current.value = es?.oldPlayerName || '';
      }
    }, 0);
  };

  const closeEditSuggestionPrep = () => {
    setEditSuggestionState({ open: false, editSuggestion: null });
  };

  const handlePrepareEditSuggestion = useCallback(async () => {
    const es = editSuggestionState.editSuggestion;
    if (!es?._id) return;
    if (!canSuggest) return;
    const id = String(es._id);
    const raw = editSuggestionCriticalRef.current?.value ?? '';
    const v = validateTermClient(raw);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }

    const key = `editSuggestion:${id}`;
    setBusyKey(key);
    try {
      const created = await createSuggestion({
        term: v.term,
        matchWholeWordOnly: prepareDialogMatchWholeWordOnly,
      });
      if (!created) {
        toast.info('Vorschlag wurde gespeichert');
        closeEditSuggestionPrep();
        return;
      }
      await markEditSuggestionHandled({ editSuggestionId: id, handledComment: v.term });
      toast.success('Vorschlag erstellt');
      closeEditSuggestionPrep();
      await Promise.all([fetchEditSuggestions(), fetchSuggestions()]);
    } catch (e) {
      const code = String(e?.code || '').trim();
      if (code === 'BLACKLIST_SUGGESTION_EXISTS') {
        toast.error('Vorschlag existiert bereits');
        return;
      }
      toast.error('Fehler beim Erstellen des Vorschlags');
    } finally {
      setBusyKey(null);
    }
  }, [
    canSuggest,
    createSuggestion,
    editSuggestionState.editSuggestion,
    fetchEditSuggestions,
    fetchSuggestions,
    markEditSuggestionHandled,
    prepareDialogMatchWholeWordOnly,
  ]);

  const handleDismissEditSuggestion = useCallback(
    async es => {
      if (!es?._id || !canSuggest) return;
      const id = String(es._id);
      const key = `editSuggestion:${id}`;
      setBusyKey(key);
      try {
        await markEditSuggestionHandled({ editSuggestionId: id, status: 'dismissed' });
        toast.success('Meldung abgelehnt');
        await fetchEditSuggestions();
      } catch (e) {
        toast.error('Fehler beim Ablehnen der Meldung');
      } finally {
        setBusyKey(null);
      }
    },
    [canSuggest, fetchEditSuggestions, markEditSuggestionHandled]
  );

  const suggestionStatusChip = s => {
    const status = String(s?.status || '').toLowerCase();
    if (status === 'approved') return <Chip size='small' label='Angenommen' color='success' variant='outlined' />;
    if (status === 'rejected') return <Chip size='small' label='Abgelehnt' color='error' variant='outlined' />;
    return <Chip size='small' label='Offen' color='warning' variant='outlined' />;
  };

  const editSuggestionStatusChip = s => {
    const status = String(s?.status || '').toLowerCase();
    if (status === 'handled') return <Chip size='small' label='Erledigt' color='success' variant='outlined' />;
    if (status === 'dismissed') return <Chip size='small' label='Abgelehnt' color='default' variant='outlined' />;
    return <Chip size='small' label='Offen' color='warning' variant='outlined' />;
  };

  const formatUserLabel = user => {
    if (!user) return '—';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.username || '—';
  };

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2 }}>
      <Typography variant='h5' sx={{ mb: 1 }}>
        Spielnamen-Blacklist
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          label={canManage ? 'Neues verbotenes Wort' : 'Vorschlag für verbotenes Wort'}
          inputRef={termInputRef}
          inputProps={{ maxLength: TERM_MAX_LENGTH }}
          size='small'
          sx={{ width: 360 }}
          disabled={!canSuggest || isLoading}
        />
        <Tooltip title='Wenn aktiv, wird nur noch geprüft, ob das Wort als eigenes Wort vorkommt (durch Leerzeichen getrennt), nicht als Teil innerhalb eines längeren Worts.'>
          <FormControlLabel
            control={
              <Switch
                checked={newEntryMatchWholeWordOnly}
                onChange={(_, v) => setNewEntryMatchWholeWordOnly(v)}
                disabled={!canSuggest || isLoading}
                size='small'
              />
            }
            label='Nur separates Wort'
            sx={{ mr: 0 }}
          />
        </Tooltip>
        <Button variant='contained' onClick={handleSubmit} disabled={isLoading || !canSuggest}>
          {canManage ? 'Hinzufügen' : 'Vorschlagen'}
        </Button>
        {isLoading && <CircularProgress size={18} />}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {canSuggest && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant='h6'>Zu bearbeitende Meldungen</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                Nur offen
              </Typography>
              <Switch
                checked={showOnlyPendingEditSuggestions}
                onChange={() => setShowOnlyPendingEditSuggestions(v => !v)}
              />
            </Box>
          </Box>

          <Table size='small' sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Alter Name</TableCell>
                {canManage && <TableCell>Filiale</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Gemeldet von</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEditSuggestions.map(es => {
                const id = es?._id ? String(es._id) : '';
                const isBusy = busyKey === `editSuggestion:${id}`;
                const createdAt = es?.meta?.createdAt ? new Date(es.meta.createdAt).toLocaleString('de-DE') : '—';
                const branchLabel =
                  es?.branchId && typeof es.branchId === 'object'
                    ? es.branchId.longName || es.branchId.locationName || '—'
                    : '—';
                const isPending = String(es?.status || '') === 'pending';
                return (
                  <TableRow key={id || es?.oldPlayerNameNormalized || es?.oldPlayerName}>
                    <TableCell>{es?.oldPlayerName || ''}</TableCell>
                    {canManage && <TableCell>{branchLabel}</TableCell>}
                    <TableCell>{editSuggestionStatusChip(es)}</TableCell>
                    <TableCell>{formatUserLabel(es?.meta?.createdBy)}</TableCell>
                    <TableCell>{createdAt}</TableCell>
                    <TableCell align='right'>
                      {isPending ? (
                        <>
                          <Tooltip title='Als Blacklist-Vorschlag aufbereiten'>
                            <IconButton onClick={() => openEditSuggestionPrep(es)} disabled={isBusy} size='small' aria-label='prepare'>
                              <EditIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Meldung ablehnen (ohne Eintrag in der Blacklist)'>
                            <IconButton
                              onClick={() => handleDismissEditSuggestion(es)}
                              disabled={isBusy}
                              size='small'
                              aria-label='dismiss-edit-suggestion'
                            >
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Typography variant='body2' color='text.secondary'>
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedEditSuggestions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 6 : 5} align='center' sx={{ color: 'text.secondary' }}>
                    Keine Meldungen
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ mb: 2 }} />
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant='h6'>Vorschläge</Typography>
        {canSuggest && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Nur offen
            </Typography>
            <Switch
              checked={showOnlyPendingSuggestions}
              onChange={() => setShowOnlyPendingSuggestions(v => !v)}
            />
          </Box>
        )}
      </Box>

      <Table size='small' sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Wort</TableCell>
            <TableCell align='center'>
              <Tooltip title='Nur separates Wort: Treffer nur, wenn der Begriff durch Leerzeichen als eigenes Wort steht (nicht als Teilstring).'>
                <span>Nur Wort</span>
              </Tooltip>
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Vorgeschlagen von</TableCell>
            <TableCell>Datum</TableCell>
            {canSuggest && <TableCell align='right'>Aktionen</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedSuggestions.map(s => {
            const id = s?._id ? String(s._id) : '';
            const isBusy = busyKey === `suggestion:${id}`;
            const requestedAt = s?.meta?.requestedAt ? new Date(s.meta.requestedAt).toLocaleString('de-DE') : '—';
            const isPending = String(s?.status || '') === 'pending';
            const canModify = isPending && (canManage || canSuggest);
            return (
              <TableRow key={id || s?.termNormalized || s?.term}>
                <TableCell>{s?.term || ''}</TableCell>
                <TableCell align='center'>
                  {canModify ? (
                    <Switch
                      checked={s?.matchWholeWordOnly === true}
                      onChange={() => handleToggleSuggestionMatchWholeWord(s)}
                      disabled={isBusy}
                      size='small'
                    />
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      {s?.matchWholeWordOnly ? 'Ja' : 'Nein'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{suggestionStatusChip(s)}</TableCell>
                <TableCell>{formatUserLabel(s?.meta?.requestedBy)}</TableCell>
                <TableCell>{requestedAt}</TableCell>
                {canSuggest && (
                  <TableCell align='right'>
                    {canModify ? (
                      <>
                        <IconButton onClick={() => openEditSuggestion(s)} disabled={isBusy} size='small' aria-label='edit'>
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteSuggestion(s)}
                          disabled={isBusy}
                          size='small'
                          aria-label='delete-suggestion'
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                        {canManage && (
                          <>
                            <IconButton
                              onClick={() => handleApproveSuggestion(s)}
                              disabled={isBusy}
                              size='small'
                              aria-label='approve'
                            >
                              <CheckIcon fontSize='small' />
                            </IconButton>
                            <IconButton
                              onClick={() => handleRejectSuggestion(s)}
                              disabled={isBusy}
                              size='small'
                              aria-label='reject'
                            >
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </>
                        )}
                      </>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        —
                      </Typography>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {sortedSuggestions.length === 0 && (
            <TableRow>
              <TableCell colSpan={canSuggest ? 6 : 5} align='center' sx={{ color: 'text.secondary' }}>
                Keine Vorschläge
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Divider sx={{ mb: 2 }} />

      <Typography variant='h6' sx={{ mb: 1 }}>
        Blacklist
      </Typography>

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Wort</TableCell>
            <TableCell align='center'>
              <Tooltip title='Nur separates Wort: Treffer nur bei eigenständigem Wort (Leerzeichen), nicht innerhalb eines anderen Worts.'>
                <span>Nur Wort</span>
              </Tooltip>
            </TableCell>
            <TableCell align='center'>Aktiv</TableCell>
            {canManage && <TableCell align='right'>Aktionen</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map(e => {
            const id = e?._id ? String(e._id) : '';
            const isBusy = busyKey === `entry:${id}`;
            return (
              <TableRow key={id || e?.termNormalized || e?.term}>
                <TableCell>{e?.term || ''}</TableCell>
                <TableCell align='center'>
                  <Switch
                    checked={e?.matchWholeWordOnly === true}
                    onChange={() => handleToggleEntryMatchWholeWord(e)}
                    disabled={isBusy || !canManage}
                    size='small'
                  />
                </TableCell>
                <TableCell align='center'>
                  <Switch
                    checked={e?.active === true}
                    onChange={() => handleToggleActive(e)}
                    disabled={isBusy || !canManage}
                  />
                </TableCell>
                {canManage && (
                  <TableCell align='right'>
                    <IconButton
                      onClick={() => handleDelete(e)}
                      disabled={isBusy || !canManage}
                      size='small'
                      aria-label='delete'
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {sortedEntries.length === 0 && (
            <TableRow>
              <TableCell colSpan={canManage ? 4 : 3} align='center' sx={{ color: 'text.secondary' }}>
                Keine Einträge
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={editState.open} onClose={closeEditSuggestion} fullWidth maxWidth='sm'>
        <DialogTitle>Vorschlag bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label='Vorschlag'
              defaultValue={editState.suggestion?.term || ''}
              inputRef={editInputRef}
              inputProps={{ maxLength: TERM_MAX_LENGTH }}
              fullWidth
              disabled={isLoading}
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={editDialogMatchWholeWordOnly}
                  onChange={(_, v) => setEditDialogMatchWholeWordOnly(v)}
                  disabled={isLoading}
                  size='small'
                />
              }
              label='Nur separates Wort'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditSuggestion} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button variant='contained' onClick={handleSaveEditedSuggestion} disabled={isLoading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editSuggestionState.open} onClose={closeEditSuggestionPrep} fullWidth maxWidth='sm'>
        <DialogTitle>Meldung aufbereiten</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Bitte gib den kritischen Teil an, der in die Blacklist übernommen werden soll.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <TextField
              label='Kritischer Teil'
              defaultValue={editSuggestionState.editSuggestion?.oldPlayerName || ''}
              inputRef={editSuggestionCriticalRef}
              inputProps={{ maxLength: TERM_MAX_LENGTH }}
              fullWidth
              disabled={isLoading}
            />
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={prepareDialogMatchWholeWordOnly}
                  onChange={(_, v) => setPrepareDialogMatchWholeWordOnly(v)}
                  disabled={isLoading}
                  size='small'
                />
              }
              label='Nur separates Wort'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditSuggestionPrep} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button variant='contained' onClick={handlePrepareEditSuggestion} disabled={isLoading}>
            Als Vorschlag speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

